'use client';

import { useCallback, useEffect, useState } from 'react';
import { pickChrome } from '../lib/siteChromeCopy';

/** localStorage key — visitor choice only (no marketing CMP). */
const SITE_COOKIE_SETTINGS_STORAGE_KEY = 'huba.site.cookie-settings.v1';

const SITE_COOKIE_SETTINGS_ACCEPTED = 'accepted';
const SITE_COOKIE_SETTINGS_REJECTED = 'rejected';

/** Footer / SSR reopen — open the cookie settings bar. */
const SITE_COOKIE_SETTINGS_OPEN_EVENT = 'ws-cookie-settings:open';

export interface SiteCookieSettingsBarProps {
  locale: string;
  /** Localized Datenschutz / Privacy href. */
  privacyHref: string;
  /**
   * Studio edit mode — do not auto-show (keeps the canvas clear).
   * Footer “Cookie settings” still opens the bar.
   */
  suppressAutoOpen?: boolean;
}

function hasStoredChoice(): boolean {
  try {
    const value = window.localStorage.getItem(SITE_COOKIE_SETTINGS_STORAGE_KEY);
    return value === SITE_COOKIE_SETTINGS_ACCEPTED || value === SITE_COOKIE_SETTINGS_REJECTED;
  } catch {
    return false;
  }
}

function persistChoice(choice: typeof SITE_COOKIE_SETTINGS_ACCEPTED | typeof SITE_COOKIE_SETTINGS_REJECTED): void {
  try {
    window.localStorage.setItem(SITE_COOKIE_SETTINGS_STORAGE_KEY, choice);
  } catch {
    /* private mode — still hide for this session */
  }
}

/**
 * Germany-market cookie settings bar — common Accept / Reject pattern.
 * Platform does not run marketing/analytics tags; choice is preference storage only.
 */
export function SiteCookieSettingsBar({
  locale,
  privacyHref,
  suppressAutoOpen = false,
}: SiteCookieSettingsBarProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const open = () => setVisible(true);
    window.addEventListener(SITE_COOKIE_SETTINGS_OPEN_EVENT, open);

    if (!suppressAutoOpen && !hasStoredChoice()) {
      setVisible(true);
    }

    return () => {
      window.removeEventListener(SITE_COOKIE_SETTINGS_OPEN_EVENT, open);
    };
  }, [suppressAutoOpen]);

  const accept = useCallback(() => {
    persistChoice(SITE_COOKIE_SETTINGS_ACCEPTED);
    setVisible(false);
  }, []);

  const reject = useCallback(() => {
    persistChoice(SITE_COOKIE_SETTINGS_REJECTED);
    setVisible(false);
  }, []);

  if (!visible) {
    return null;
  }

  return (
    <div
      className="ws-cookie-settings"
      role="region"
      aria-label={pickChrome('cookieSettingsRegion', locale)}
    >
      <p className="ws-cookie-settings__body">
        {pickChrome('cookieSettingsBody', locale)}{' '}
        <a href={privacyHref} className="ws-cookie-settings__link">
          {pickChrome('cookieSettingsPolicyLink', locale)}
        </a>
      </p>
      <div className="ws-cookie-settings__actions">
        <button type="button" className="ws-cookie-settings__reject" onClick={reject}>
          {pickChrome('cookieSettingsReject', locale)}
        </button>
        <button type="button" className="ws-cookie-settings__accept" onClick={accept}>
          {pickChrome('cookieSettingsAccept', locale)}
        </button>
      </div>
    </div>
  );
}

/** Dispatch reopen from footer Cookie-Einstellungen control. */
export function openSiteCookieSettings(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new Event(SITE_COOKIE_SETTINGS_OPEN_EVENT));
}
